import NavLink from "@/Components/NavLink";
import { getIcon } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
    resetEntireApp,
    toggleMobileMenu,
} from "@/redux/slices/appSettingSlice";
import { setLocale } from "@/redux/slices/localeSlice";
import { Dialog, Menu, Popover, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link, router, usePage } from "@inertiajs/react";
import { isNull } from "lodash";
import {
    BookOpenIcon,
    Check,
    ChevronDown,
    ClipboardListIcon,
    FileTextIcon,
    GraduationCapIcon,
    HelpCircle,
    HomeIcon,
    LogOutIcon,
    MailIcon,
    ReceiptIcon,
    ServerIcon,
    SettingsIcon,
    TreeDeciduousIcon,
    UserIcon,
} from "lucide-react";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactSVG } from "react-svg";

export default function (): React.ReactNode {
    const {
        props: { auth, currentLang, setting, currentRouteName },
    }: any = usePage();
    const {
        appSetting: { mobileMenuOpen },
        locale: { otherLang, dir },
    } = useAppSelector((state) => state);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);

    const handleChangeLang = async () => {
        dispatch(setLocale(otherLang));
        router.visit(currentRouteName[otherLang] ?? currentRouteName, {
            preserveScroll: true,
        });
    };

    useEffect(() => {
        if (mobileMenuOpen) {
            dispatch(toggleMobileMenu());
        }
    }, [currentLang, currentRouteName]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <header
            className={`sticky inset-x-0 top-4 z-50 rounded-full px-6 py-2 transition-all duration-500 ${
                isScrolled
                    ? "backdrop-blur-3xl bg-white/60 shadow-2xl shadow-gray-300/60 border-2 border-white/40"
                    : "backdrop-blur-md bg-white/95"
            }`}
        >
            <nav
                className="flex items-center justify-start"
                aria-label="Global"
            >
                <div className="flex sm:me-6 border-black">
                    <Link
                        href={route(`${currentLang}.frontend.home`)}
                        className="-m-1.5 p-1.5 w-20 md:w-32"
                    >
                        <span className="sr-only">{setting.name}</span>
                        <img
                            className="h-10 w-auto object-contain"
                            src={
                                currentLang == "ar"
                                    ? "/images/logo_arabic.png"
                                    : "/images/logo-en.png"
                            }
                            alt={setting.name}
                        />
                    </Link>
                </div>
                <div className="flex w-full justify-end items-center lg:hidden">
                    <button
                        type="button"
                        className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                        onClick={() => dispatch(toggleMobileMenu())}
                    >
                        <span className="sr-only">Open main menu</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </button>
                </div>
                <div className="hidden print:hidden lg:flex flex-1 justify-start items-center lg:gap-x-6">
                    {/* <NavLink
                        href={route(`${currentLang}.frontend.home`)}
                        data-state={
                            route().current(`${currentLang}.frontend.home`)
                                ? `active`
                                : `none`
                        }
                        className="capitalize text-sm font-semibold leading-12 text-gray-600 "
                    >
                        {t("home")}
                    </NavLink> */}
                    <NavLink
                        href={route(`${currentLang}.frontend.course.index`)}
                        data-state={
                            route().current(
                                `${currentLang}.frontend.course.index`,
                            )
                                ? `active`
                                : `none`
                        }
                        className="hidden capitalize text-sm font-semibold leading-12 text-gray-600 "
                    >
                        {t("courses")}
                    </NavLink>
                    {/* services (docs & questionnaires) */}
                    <Popover className="relative">
                        <Popover.Button
                            className={
                                "flex flex-row justify-center items-center gap-x-2 capitalize text-sm font-semibold leading-12 text-gray-600"
                            }
                        >
                            <div>{t("services")}</div>
                            <div>
                                <ChevronDown className="w-4 h-4 text-gray-700" />
                            </div>
                        </Popover.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                        >
                            <Popover.Panel className="absolute  z-10 mt-5 flex w-screen max-w-max rtl:translate-x-10 ltr:-translate-x-10 px-4">
                                <div className="w-screen max-w-sm flex-auto overflow-hidden rounded-3xl bg-white text-sm leading-6 shadow-lg ring-1 ring-gray-900/5">
                                    <div className="p-4 grid grid-cols-1 gap-x-2 capitalize">
                                        <Link
                                            preserveScroll
                                            href={route(
                                                `${currentLang}.frontend.course.index`,
                                            )}
                                            className="group relative flex justify-start items-center gap-x-6 rounded-lg p-2 hover:bg-gray-50 w-full"
                                        >
                                            <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                                                <GraduationCapIcon className="size-7 text-gray-600 group-hover:text-theme-600" />
                                            </div>
                                            <div>
                                                <Link
                                                    preserveScroll
                                                    href={route(
                                                        `${currentLang}.frontend.course.index`,
                                                    )}
                                                    className="normal-text "
                                                >
                                                    {t("courses")}
                                                    <span className="absolute inset-0" />
                                                </Link>
                                            </div>
                                        </Link>
                                        <Link
                                            preserveScroll
                                            href={route(
                                                `${currentLang}.frontend.questionnaire.index`,
                                            )}
                                            className="group relative flex justify-start items-center gap-x-6 rounded-lg p-2 hover:bg-gray-50 w-full"
                                        >
                                            <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                                                <ClipboardListIcon className="size-7 text-gray-600 group-hover:text-theme-600" />
                                            </div>
                                            <div>
                                                <Link
                                                    preserveScroll
                                                    href={route(
                                                        `${currentLang}.frontend.questionnaire.index`,
                                                    )}
                                                    className="normal-text "
                                                >
                                                    {t("universities_forms")}
                                                    <span className="absolute inset-0" />
                                                </Link>
                                            </div>
                                        </Link>
                                        <Link
                                            preserveScroll
                                            href={route(
                                                `${currentLang}.frontend.document.index`,
                                            )}
                                            className="group relative flex justify-start items-center gap-x-6 rounded-lg p-2 hover:bg-gray-50 w-full"
                                        >
                                            <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                                                <BookOpenIcon className="size-7 text-gray-600 group-hover:text-theme-600" />
                                            </div>
                                            <div>
                                                <Link
                                                    href={route(
                                                        `${currentLang}.frontend.document.index`,
                                                    )}
                                                    className="normal-text "
                                                >
                                                    {t("books")}
                                                    <span className="absolute inset-0" />
                                                </Link>
                                            </div>
                                        </Link>
                                        {/* <Link
                                            preserveScroll
                                            href={route(
                                                `${currentLang}.frontend.questionnaire.index`,
                                            )}
                                            className="group relative flex justify-start items-center gap-x-6 rounded-lg p-2 hover:bg-gray-50 w-full"
                                        >
                                            <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                                                <ReactSVG
                                                    className="h-6 w-6 text-gray-600 group-hover:text-theme-600"
                                                    src={getIcon(`item.svg`)}
                                                />
                                            </div>
                                            <div>
                                                <Link
                                                    preserveScroll
                                                    href={route(
                                                        `${currentLang}.frontend.course.index`,
                                                        {
                                                            group: 'online',
                                                            is_ticket: 1,
                                                        },
                                                    )}
                                                    className="normal-text "
                                                >
                                                    {t('online_consultancies')}
                                                    <span className="absolute inset-0" />
                                                </Link>
                                            </div>
                                        </Link> */}
                                    </div>
                                </div>
                            </Popover.Panel>
                        </Transition>
                    </Popover>
                    <NavLink
                        href={route(`${currentLang}.frontend.tree.index`)}
                        data-state={
                            route().current(
                                `${currentLang}.frontend.tree.index`,
                            )
                                ? `active`
                                : `none`
                        }
                        className="capitalize text-sm font-semibold leading-12 text-gray-600 flex items-center gap-x-2"
                    >
                        <HelpCircle className="size-4" />
                        {t("trees")}
                    </NavLink>
                    <NavLink
                        href={route(`${currentLang}.frontend.contactus`)}
                        data-state={
                            route().current(`${currentLang}.frontend.contactus`)
                                ? `active`
                                : `none`
                        }
                        className="capitalize text-sm font-semibold leading-12 text-gray-600 flex items-center gap-x-2"
                    >
                        <MailIcon className="size-4" />
                        {t("contactus")}
                    </NavLink>
                </div>
                <div className={"mx-2 xl:mx-4"}>
                    <button
                        onClick={() => handleChangeLang()}
                        className="flex items-center justify-center gap-x-2.5 p-3 font-semibold text-prim-900 hover:bg-theme-100 rounded-full"
                    >
                        <ReactSVG
                            className="h-4 w-4 text-gray-600 group-hover:text-theme-600"
                            src={getIcon("globe.svg")}
                        />
                        <div className={"capitalize hidden sm:flex"}>
                            {t(otherLang)}
                        </div>
                    </button>
                </div>

                {/* Profile dropdown */}
                {!isNull(auth) ? (
                    <Menu as="div" className="relative">
                        <Menu.Button
                            className={
                                "flex flex-row justify-evenly items-center gap-x-2 z-50"
                            }
                        >
                            <div>
                                <img
                                    src={auth.image}
                                    className="w-8 h-8 rounded-full border border-gray-200"
                                />
                            </div>
                            <div className="truncate text-gray-600 text-xs">
                                {auth.name}
                            </div>
                            <div>
                                <ChevronDown className="w-3 h-3 text-gray-600" />
                            </div>
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute ltr:right-0 rtl:left-0 z-10 mt-2.5 w-60 origin-top-right rounded-xl bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none divide-y divide-gray-100 z-50">
                                <Menu.Item>
                                    <Link
                                        href={route(
                                            `${currentLang}.frontend.profile.course`,
                                        )}
                                        className={
                                            "flex items-center gap-x-3 px-3 py-3 text-sm leading-6 text-prim-900 hover:bg-gray-100 capitalize"
                                        }
                                    >
                                        <GraduationCapIcon className="size-4" />
                                        {`${t("my_courses")}`}
                                    </Link>
                                </Menu.Item>
                                <Menu.Item>
                                    <Link
                                        href={route(
                                            `${currentLang}.frontend.profile.document`,
                                        )}
                                        className={
                                            "flex items-center gap-x-3 px-3 py-3 text-sm leading-6 text-prim-900 hover:bg-gray-100 capitalize"
                                        }
                                    >
                                        <BookOpenIcon className="size-4" />
                                        {`${t("my_documents")}`}
                                    </Link>
                                </Menu.Item>
                                <Menu.Item>
                                    <Link
                                        href={route(
                                            `${currentLang}.frontend.profile.questionnaire`,
                                        )}
                                        className={
                                            "flex items-center gap-x-3 px-3 py-3 text-sm leading-6 text-prim-900 hover:bg-gray-100 capitalize"
                                        }
                                    >
                                        <ClipboardListIcon className="size-4" />
                                        {`${t("my_questionnaires")}`}
                                    </Link>
                                </Menu.Item>
                                <Menu.Item>
                                    <Link
                                        href={route(
                                            `${currentLang}.frontend.profile.invoice`,
                                        )}
                                        className={
                                            "flex items-center gap-x-3 px-3 py-3 text-sm leading-6 text-prim-900 hover:bg-gray-100 capitalize"
                                        }
                                    >
                                        <ReceiptIcon className="size-4" />
                                        {`${t("my_invoices")}`}
                                    </Link>
                                </Menu.Item>
                                {auth.isAdmin || auth.isTeacher ? (
                                    <Menu.Item>
                                        <Link
                                            href={route(
                                                `${currentLang}.backend.home`,
                                            )}
                                            className={
                                                "flex items-center gap-x-3 px-3 py-3 text-sm leading-6 text-prim-900 hover:bg-gray-100 capitalize"
                                            }
                                        >
                                            <UserIcon className="size-4" />
                                            {`${t("dashboard")} - (${t(auth.roles.name)})`}
                                        </Link>
                                    </Menu.Item>
                                ) : null}
                                <Menu.Item>
                                    <Link
                                        href={route(
                                            `${currentLang}.frontend.profile.edit`,
                                            auth.id,
                                        )}
                                        className={
                                            "flex items-center gap-x-3 px-3 py-3 text-sm leading-6 text-prim-900 hover:bg-gray-100 capitalize"
                                        }
                                    >
                                        <SettingsIcon className="size-4" />
                                        {`${t("edit")} ${t("password")}`}
                                    </Link>
                                </Menu.Item>
                                <button
                                    className={
                                        "w-full flex items-center gap-x-3 rtl:text-right ltr:text-left px-3 py-3 text-sm leading-6 text-prim-900 hover:bg-gray-100"
                                    }
                                    onClick={() => {
                                        dispatch(resetEntireApp());
                                        router.post(
                                            route(`${currentLang}.logout`),
                                        );
                                    }}
                                >
                                    <LogOutIcon className="size-4" />
                                    {t("logout")}
                                </button>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                ) : (
                    <div className="hidden lg:flex lg:justify-end border border-theme-700 rounded-3xl">
                        <Link
                            href={route(`${currentLang}.login`)}
                            className="text-center p-3 px-6 w-1/2 border-e border-gray-100 text-theme-700 hover:bg-theme-700 hover:text-white capitalize text-sm lg:text-normal rounded-s-3xl truncate"
                        >
                            {t("login")}
                        </Link>
                        <Link
                            href={route(`${currentLang}.register`)}
                            className="text-center p-3 px-6 w-1/2  text-theme-700 hover:bg-theme-700 hover:text-white capitalize text-sm lg:text-normal rounded-e-3xl truncate"
                        >
                            {t("new_user")}
                        </Link>
                    </div>
                )}
            </nav>
            {/* mobile */}
            <Dialog
                as="div"
                className="lg:hidden"
                open={mobileMenuOpen}
                onClose={() => dispatch(toggleMobileMenu)}
                dir={dir}
            >
                <div className="fixed inset-0 z-50" />
                <Dialog.Panel className="fixed inset-y-0 rtl:right-0 ltr:left-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                    <div className="flex items-center justify-between">
                        <Link
                            preserveScroll
                            href={route(`${currentLang}.frontend.home`)}
                            className="-m-1.5 p-1.5"
                        >
                            <span className="sr-only">{setting.name}</span>
                            <img
                                className="w-auto h-10"
                                src={
                                    currentLang == "ar"
                                        ? "/images/logo_arabic.png"
                                        : "/images/logo-en.png"
                                }
                                alt={setting.name}
                            />
                        </Link>
                        <button
                            type="button"
                            className="-m-2.5 rounded-md p-2.5 text-gray-700"
                            onClick={() => dispatch(toggleMobileMenu())}
                        >
                            <span className="sr-only">Close menu</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="mt-6 flow-root">
                        <div className="-my-6 divide-y divide-gray-500/10">
                            <div className="flex flex-col justify-start items-center gap-y-2 py-6">
                                <Link
                                    preserveScroll
                                    href={route(`${currentLang}.frontend.home`)}
                                    className="-mx-3 w-full rtl:text-right ltr:text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50 flex items-center gap-x-3"
                                >
                                    <HomeIcon className="size-5" />
                                    {t("home")}
                                </Link>
                                <Link
                                    preserveScroll
                                    href={route(
                                        `${currentLang}.frontend.course.index`,
                                    )}
                                    className="-mx-3 w-full rtl:text-right ltr:text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50 flex items-center gap-x-3"
                                >
                                    <GraduationCapIcon className="size-5" />
                                    {t("courses")}
                                </Link>
                                <Link
                                    preserveScroll
                                    href={route(
                                        `${currentLang}.frontend.document.index`,
                                    )}
                                    className="-mx-3 w-full rtl:text-right ltr:text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50 flex items-center gap-x-3"
                                >
                                    <BookOpenIcon className="size-5" />
                                    {t("books")}
                                </Link>
                                <Link
                                    preserveScroll
                                    href={route(
                                        `${currentLang}.frontend.tree.index`,
                                    )}
                                    data-state={
                                        route().current(
                                            `${currentLang}.frontend.tree.index`,
                                        )
                                            ? `active`
                                            : `none`
                                    }
                                    className="-mx-3 w-full rtl:text-right ltr:text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50 flex items-center gap-x-3"
                                >
                                    <HelpCircle className="size-5" />
                                    {t("trees")}
                                </Link>
                                <Link
                                    preserveScroll
                                    href={route(
                                        `${currentLang}.frontend.questionnaire.index`,
                                    )}
                                    className="-mx-3 w-full rtl:text-right ltr:text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50 flex items-center gap-x-3"
                                >
                                    <ClipboardListIcon className="size-5" />
                                    {t("universities_forms")}
                                </Link>
                                <Link
                                    preserveScroll
                                    href={route(
                                        `${currentLang}.frontend.contactus`,
                                    )}
                                    className="-mx-3 w-full rtl:text-right ltr:text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50 flex items-center gap-x-3"
                                >
                                    <MailIcon className="size-5" />
                                    {t("contactus")}
                                </Link>
                                <Link
                                    preserveScroll
                                    href={route(
                                        `${currentLang}.frontend.terms`,
                                    )}
                                    className="-mx-3 w-full rtl:text-right ltr:text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50 flex items-center gap-x-3"
                                >
                                    <FileTextIcon className="size-5" />
                                    {t("the_terms_and_conditions")}
                                </Link>
                                <div className={"mx-2 xl:mx-4 w-full"}>
                                    <Menu as="div" className="relative w-full">
                                        <Menu.Button className="flex w-full items-center justify-center gap-x-2.5 p-3 font-semibold text-prim-900 hover:bg-theme-100 rounded-full">
                                            <ReactSVG
                                                className="h-4 w-4 text-gray-600"
                                                src={getIcon("globe.svg")}
                                            />
                                            <span className="capitalize">
                                                {t(currentLang)}
                                            </span>
                                            <ChevronDown className="size-4 text-gray-500" />
                                        </Menu.Button>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-100"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <Menu.Items className="absolute z-50 mt-2 w-44 origin-top rounded-xl bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none ltr:right-0 rtl:left-0">
                                                {(["en", "ar"] as const).map(
                                                    (lang) => (
                                                        <Menu.Item key={lang}>
                                                            {({ active }) => (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (
                                                                            lang !==
                                                                            currentLang
                                                                        ) {
                                                                            handleChangeLang();
                                                                        }
                                                                    }}
                                                                    className={`flex w-full items-center gap-x-2 rtl:text-right ltr:text-left px-3 py-2 text-sm font-semibold ${
                                                                        active
                                                                            ? "bg-theme-50 text-theme-700"
                                                                            : "text-prim-900"
                                                                    }`}
                                                                >
                                                                    <Check
                                                                        className={`size-4 ${
                                                                            currentLang ===
                                                                            lang
                                                                                ? "opacity-100 text-theme-600"
                                                                                : "opacity-0"
                                                                        }`}
                                                                    />
                                                                    <span className="capitalize">
                                                                        {t(lang)}
                                                                    </span>
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ),
                                                )}
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                </div>
                            </div>
                            {isNull(auth) ? (
                                <div className="py-6">
                                    <Link
                                        preserveScroll
                                        href={route(`${currentLang}.login`)}
                                        className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50"
                                    >
                                        {t("login")}
                                    </Link>
                                    <Link
                                        preserveScroll
                                        href={route(`${currentLang}.register`)}
                                        className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50"
                                    >
                                        {t("register_now")}
                                    </Link>
                                </div>
                            ) : (
                                <div className="py-6">
                                    <Link
                                        preserveScroll
                                        href={route(
                                            `${currentLang}.frontend.profile.invoice`,
                                        )}
                                        className="-mx-3 flex items-center gap-x-3 rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50"
                                    >
                                        <ReceiptIcon className="size-5" />
                                        {t("my_invoices")}
                                    </Link>
                                    <Link
                                        preserveScroll
                                        href={route(
                                            `${currentLang}.frontend.profile.course`,
                                        )}
                                        className="-mx-3 flex items-center gap-x-3 rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50"
                                    >
                                        <GraduationCapIcon className="size-5" />
                                        {t("my_courses")}
                                    </Link>
                                    <Link
                                        preserveScroll
                                        href={route(
                                            `${currentLang}.frontend.profile.document`,
                                        )}
                                        className="-mx-3 flex items-center gap-x-3 rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50"
                                    >
                                        <BookOpenIcon className="size-5" />
                                        {t("my_documents")}
                                    </Link>
                                    <Link
                                        preserveScroll
                                        href={route(
                                            `${currentLang}.frontend.profile.questionnaire`,
                                        )}
                                        className="-mx-3 flex items-center gap-x-3 rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-prim-900 hover:bg-gray-50"
                                    >
                                        <ClipboardListIcon className="size-5" />
                                        {t("my_questionnaires")}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </Dialog.Panel>
            </Dialog>
        </header>
    );
}
